import {
    XyDataSeries,
    HorizontalLineAnnotation,
    EAnnotationLayer,
    SciChartSurface,
    ZoomPanModifier,
    MouseWheelZoomModifier,
    ZoomExtentsModifier,
    RolloverModifier,
    RubberBandXyZoomModifier,
    EXyDirection,
    EExecuteOn,
    FastImpulseRenderableSeries,
    LegendModifier,
    EAutoRange,
    NumberRange,
    EAxisAlignment,
    NumericAxis,
    DateTimeNumericAxis,
    CursorModifier,
    IRenderableSeries,
    FastLineRenderableSeries,
    IDataSeries,
    ISciChartSurfaceBase,
    AUTO_COLOR,
    TrianglePointMarker,
    UpdateSuspender,
    } from "scichart";
  import { IInitResult, TInitFunction } from "scichart-react";
  import { ColumnPaletteProviderActivityChartAsset } from "../PaletteProvider";
  import moment from "moment";
  import { axisLabelDateFormatter } from "../../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter";
  import { GetChartTheme } from "../../../../Shared/SciChart/ChartConfiguration";
import { LocalizationService } from "@progress/kendo-react-intl";
import { DateTickProvider } from "../../../../Shared/SciChart/SmartAxis/DateTickProvider";
import { SmartDateTZLabelProvider } from "../../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider";
import { createDateTzConverter } from "../../../../Shared/SciChart/SmartAxis/timezones";
  
  export interface TAssetActivityChartConfigResult<TSurface extends ISciChartSurfaceBase> extends IInitResult<TSurface> {
    setAutoRange: (value: boolean) => void,
    changeChartType: (viewType: string, visibleSeriesCompliance) => void
  }
  
  export type TAssetActivityConfigFunc = TInitFunction<SciChartSurface, TAssetActivityChartConfigResult<SciChartSurface>>;

  export interface Props {
    chartData: any
  }

  export interface ChartOptions {
    isAutoRange: boolean;
    isBarChartOn: boolean;
    visibleRange: { min: number | undefined; max: number | undefined; };
    setVisibleRange: (range: any) => void;
    setVisibleSeriesAsset: (series: any, isChecked: boolean) => void;
    visibleSeriesAsset: { [key: string]: boolean;}
    aggregator: string;
    viewType: string;
  }
  
  export const getChartAPI = (props:Props, chartOptions:ChartOptions,  localizationService: LocalizationService, currentLanguage: { locale: string; localeId: string; }) => {

    const endFilterDateUnix = moment(props.chartData.dateFilter.endDate).unix();
    const startFilterDateUnix = moment(props.chartData.dateFilter.startDate).unix();

    const theme = GetChartTheme();  
    
    // Use the same color palette as the pressure chart for consistency
    const tableau20 = [
      "#7B2CBF", // 6 Deep Purple
      "#0066FF", // 1 Bold Blue
      "#FF0000", // 2 Strong Red
      "#00CC00", // 3 Pure Green
      "#FF00CC", // 4 Magenta
      "#00DDFF", // 5 Sky Cyan
      "#000000",  // Black
      "#FF8800", // 7 Tangerine
      "#004D99", // 8 Navy Blue
      "#CC00FF", // 9 Vivid Violet
      "#00FF66", // 10 Mint Green
      "#FF0066", // 11 Hot Pink
      "#66FF00", // 13 Lime Green
      "#0099CC", // 14 Aqua Blue
      "#CC0000", // 15 Deep Red
      "#666666", // 16 Bold Gray
      "#6A1B9A", // 17 Dark Purple
      "#FFCC00", // 18 Golden Yellow
      "#3366FF", // 19 Bright Blue
      "#228B22", // 20 Forest Green
    ];
    
    theme.strokePalette = tableau20;
    theme.fillPalette = tableau20;

    let setAutoRangeCallback: (arg0: boolean) => void;
    let changeChartTypeCallback: (arg0: string, arg1:any) => void;
  
    const configAfterInit = (initResult) => {
        setAutoRangeCallback = initResult.setAutoRange;
        changeChartTypeCallback = initResult.changeChartType;
    } 
  
    const setAutoRange = (value: boolean) => {
        if (setAutoRangeCallback !== undefined) {
            setAutoRangeCallback(value);
        }
    }
  
    const changeChartType = (viewType: string, visibleSeries) => {
      if (changeChartTypeCallback !== undefined) {
        changeChartTypeCallback(viewType, visibleSeries);
      }
    }

    const initChart : TAssetActivityConfigFunc = async (rootElement: string | HTMLDivElement) => {
        const {sciChartSurface, wasmContext} = await SciChartSurface.create(rootElement, {theme: theme});

      // #region Setup the XAxis
       const xAxis = new DateTimeNumericAxis(wasmContext, { 
         drawMajorBands: false, 
         drawMinorGridLines: false, 
         drawMajorGridLines: false, 
         visibleRange: new NumberRange(chartOptions.visibleRange?.min, chartOptions.visibleRange?.max),
         visibleRangeLimit: new NumberRange(startFilterDateUnix, endFilterDateUnix),
         growBy: new NumberRange(0.05, 0.05),
         labelStyle: {fontFamily:"Roboto", fontSize:12,}},
         );
     
        // Overrides getDeltaFromRange to get nice major and minor deltas for the ticks
        xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
       
        // Setting timezone to local system
        const updateSettings = (xAxis$: NumericAxis) => {
          const timezone = moment.tz.guess();
          // Calculate ticks which respect the time zone
          xAxis$.tickProvider = new DateTickProvider(wasmContext, timezone);
          // Draws nice axis labels timezone and locale specific
          xAxis$.labelProvider = new SmartDateTZLabelProvider(timezone,true, currentLanguage?.localeId);
          const dateTzConverter = createDateTzConverter(timezone);
          // This is needed for Cursor and Rollover chart modifiers
          xAxis$.labelProvider.formatCursorLabel = timestamp =>
            chartOptions.aggregator === 'none' ? axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter, currentLanguage?.localeId) : axisLabelDateFormatter.toFullDateOnly(timestamp, dateTzConverter, currentLanguage?.localeId);
          };
          // Setting timezone to local system
          updateSettings(xAxis);
     
        xAxis.visibleRangeChanged.subscribe((args) => { chartOptions.setVisibleRange(args?.visibleRange); });

        sciChartSurface.xAxes.add(xAxis);
        // #endregion
        
      // #region Setup the YAxis
      // Check if we have any EndpointBF endpoints and if ALL are EndpointBF
      const hasEndpointBF = props.chartData.dataChart.some(endpoint => 
        endpoint.endpointType === 'EndpointBF'
      );
      const allAreEndpointBF = props.chartData.dataChart.every(endpoint => 
        endpoint.endpointType === 'EndpointBF'
      );

      // Create the main volume axis
      const yAxis = new NumericAxis(wasmContext,{
        axisAlignment: EAxisAlignment.Right, 
        drawMajorBands: false,
        drawMinorGridLines: false, 
        drawMajorGridLines: true, 
        labelPostfix: " cc", 
        labelPrecision: 2,
        growBy: new NumberRange(0, 0.05),
        labelStyle: {fontFamily:"Roboto", fontSize:12},
        id: "volumeAxis",
        
      });

      sciChartSurface.yAxes.add(yAxis);

      // Create a separate volume axis for EndpointBF only if we have mixed endpoint types
      let yAxisBF: NumericAxis | null = null;
      if (hasEndpointBF && !allAreEndpointBF) {
        yAxisBF = new NumericAxis(wasmContext,{
          axisAlignment: EAxisAlignment.Left, 
          drawMajorBands: false,
          drawMinorGridLines: false, 
          drawMajorGridLines: false, 
          labelPostfix: " cc", 
          labelPrecision: 2,
          growBy: new NumberRange(0, 0.05),
          labelStyle: {fontFamily:"Roboto", fontSize:12},
          id: "volumeBFAxis"
        });
        sciChartSurface.yAxes.add(yAxisBF);
      }

      // Only add zero lines and alignment logic if BF axis exists
      if (yAxisBF) {
        // Add zero line annotations to both axes for perfect visual alignment
        const zeroLineRight = new HorizontalLineAnnotation({
          y1: 0,
          yAxisId: "volumeAxis", // Link to right axis
          stroke: "gray",
          strokeThickness: 1,
          annotationLayer: EAnnotationLayer.BelowChart
        });

        const zeroLineLeft = new HorizontalLineAnnotation({
          y1: 0,
          yAxisId: "volumeBFAxis", // Link to left axis
          stroke: "gray", 
          strokeThickness: 1,
          annotationLayer: EAnnotationLayer.BelowChart
        });

        // Add both zero lines to the chart
        sciChartSurface.annotations.add(zeroLineRight);
        sciChartSurface.annotations.add(zeroLineLeft);

        // Zero alignment logic - only active when auto-range is OFF
        if (yAxisBF) {
          // Only add zero line for BF axis - main axis will be aligned to it
          const zeroLineBF = new HorizontalLineAnnotation({
            y1: 0,
            yAxisId: "volumeBFAxis",
            stroke: "gray",
            strokeThickness: 1,
            annotationLayer: EAnnotationLayer.BelowChart
          });
          
          sciChartSurface.annotations.add(zeroLineBF);

          // Zero alignment function
          const alignZerosSimple = (sourceAxis: NumericAxis, targetAxis: NumericAxis) => {
            try {
              const sourceVisible = sourceAxis.visibleRange;
              const targetVisible = targetAxis.visibleRange;

              // Calculate zero alignment when source axis has negative values
              if (sourceVisible.min < 0) {
                // Calculate where zero is positioned in source axis (as a fraction)
                const sourceZeroPosition = (0 - sourceVisible.min) / (sourceVisible.max - sourceVisible.min);

                // Target axis maintains its natural range but aligns zero proportionally
                const targetMax = targetVisible.max;
                let targetMin = -(targetMax * sourceZeroPosition / (1 - sourceZeroPosition));
                
                // Ensure target axis shows negative values
                if (targetMin > 0) {
                  targetMin = -Math.abs(targetMin);
                }
                
                targetAxis.visibleRange = new NumberRange(targetMin, targetMax);
              }
            } catch (error) {
              console.error('Error in zero alignment:', error);
            }
          };

          // Only apply alignment when auto-range is OFF
          if (!chartOptions.isAutoRange) {
            // Initial alignment
            setTimeout(() => {
              if (yAxisBF) {
                alignZerosSimple(yAxisBF as NumericAxis, yAxis);
              }
            }, 50);

            // Subscribe to changes for re-alignment
            yAxis.visibleRangeChanged.subscribe(() => {
              if (!chartOptions.isAutoRange) {
                setTimeout(() => {
                  if (yAxisBF) {
                    alignZerosSimple(yAxisBF as NumericAxis, yAxis);
                  }
                }, 10);
              }
            });
          }
        }
              }
        
        // Only add zero line for main axis when no BF axis exists (no alignment needed)
        if (!yAxisBF) {
          const zeroLineMainAlways = new HorizontalLineAnnotation({
            y1: 0,
            yAxisId: "volumeAxis",
            stroke: "gray",
            strokeThickness: 1,
            annotationLayer: EAnnotationLayer.BelowChart
          });
          sciChartSurface.annotations.add(zeroLineMainAlways);
        }
        
        // #endregion

      // #region Setup the Chart Modifers
      const legend = new LegendModifier({
        placementDivId: "legend-asset",
        showCheckboxes: true,
        showSeriesMarkers: true,
        showLegend: true,
        // Subscribe to checked changed here
        isCheckedChangedCallback: (series: IRenderableSeries, isChecked: boolean) => {
         // Zoom extents for the specific y-axis this series belongs to
         const yAxisId = series.yAxisId || "volumeAxis";
         const yAxis = sciChartSurface.yAxes.getById(yAxisId);
         if (yAxis) {
           yAxis.autoRange = EAutoRange.Always;
         }
         chartOptions.setVisibleSeriesAsset(series.id, isChecked)  
        }
      });
    
      sciChartSurface.chartModifiers.add(legend);

      sciChartSurface.chartModifiers.add(
        new ZoomPanModifier({xyDirection: EXyDirection.XDirection}),
        new MouseWheelZoomModifier({xyDirection:EXyDirection.XDirection}),
        new ZoomExtentsModifier({xyDirection:EXyDirection.XyDirection}),
        new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton }),
        new CursorModifier({
          showTooltip: false, 
          hitTestRadius: 10, 
          tooltipContainerBackground: "#424242", 
          axisLabelFill: "#424242", 
          id: "AssetModifierGroup",
          showYLine: true,
          showAxisLabels: true
        }),
        new RolloverModifier({
          hitTestRadius: 10, 
          showTooltip: true, 
          snapToDataPoint: true, 
          showRolloverLine: false, 
          id: "AssetRollOver",
          tooltipDataTemplate: (seriesInfo: any) => {
            const lines: string[] = [];
            lines.push(seriesInfo.seriesName);
            lines.push(`X: ${seriesInfo.formattedXValue}`);
            
            // Only show the Y-axis value that corresponds to this series
            if (seriesInfo.series && seriesInfo.series.yAxisId) {
              const yValue = seriesInfo.formattedYValue;
              // Determine the appropriate label based on the axis ID
              let axisLabel = '';
              if (seriesInfo.series.yAxisId === 'volumeBFAxis') {
                axisLabel = ' cc';
              } else if (seriesInfo.series.yAxisId === 'volumeAxis') {
                axisLabel = ' cc';
              }
              lines.push(`Y: ${yValue}${axisLabel}`);
            } else {
              lines.push(`Y: ${seriesInfo.formattedYValue}`);
            }
            
            return lines;
          }
        }),
      );
      
      // #endregion
      
      // #region Create Data Series and Setup RenderableSeries

      // Create a series that represents the entire filter period so the chart displays the entire range. 
      const filterPeriodSeries = new FastLineRenderableSeries(wasmContext, {
        id: "FilterPeriodSeriesId", 
        isVisible: false,
        dataSeries: new XyDataSeries(wasmContext, {xValues: [startFilterDateUnix, endFilterDateUnix],yValues: [0,0], dataSeriesName: "filterPeriodSeries"}),
        yAxisId: "volumeAxis" // Explicitly assign to the main volume axis
      });  

      sciChartSurface.renderableSeries.add(filterPeriodSeries);
      legend.includeSeries(filterPeriodSeries, false);

      const createVolumeDispensedSeries = ((id: string, viewType: string, isVisible: boolean, dataSeries: IDataSeries) => {
        let volumeDispensed;
        
        switch(viewType) {
          case 'bar':
          case 'userBar':
            volumeDispensed = new FastImpulseRenderableSeries(wasmContext, {
              stroke: AUTO_COLOR, 
              fill: AUTO_COLOR,
              pointMarker: new TrianglePointMarker(wasmContext, {
                width: 12, 
                height: 12, 
                stroke: "rgba(50, 53, 67,1)", 
                fill: "rgba(50, 53, 67,1)"
              }),
              paletteProvider: new ColumnPaletteProviderActivityChartAsset(),
              id: id,
              dataSeries: dataSeries,
              isVisible: isVisible
            });
            break;
          case 'line':
          case 'userLine':
          default:
            volumeDispensed = new FastLineRenderableSeries(wasmContext, {
              stroke: AUTO_COLOR,
              pointMarker: new TrianglePointMarker(wasmContext, {width: 12, height: 12, stroke: "rgba(50, 53, 67,1)", fill: "rgba(50, 53, 67,1)"}),
              paletteProvider: new ColumnPaletteProviderActivityChartAsset(),
              id: id,
              dataSeries: dataSeries,
              isVisible: isVisible
            });
            break;
        }

        volumeDispensed.rolloverModifierProps.tooltipTextColor = 'White'
        volumeDispensed.rolloverModifierProps.showRollover = true
        volumeDispensed.rolloverModifierProps.markerColor = 'red'

        return volumeDispensed;
      });

      for(let i = 0; i < props.chartData.dataChart.length ; i++){
          const endpoint = props.chartData.dataChart[i];
          const isEndpointBF = endpoint.endpointType === 'EndpointBF';
          
          const volumeDispensedDataSeries = new XyDataSeries(wasmContext, {
              xValues: endpoint.timestamps, 
              yValues: endpoint.volumeDispensedData,
              dataSeriesName: props.chartData.dataChart.length > 1 ? endpoint.endpointName  + ` [Volume] (cc)` : `Volume (cc)`
            });
          
          const volumeDispensed = createVolumeDispensedSeries(
            endpoint.endpointId, 
            chartOptions.viewType, 
            chartOptions.visibleSeriesAsset[endpoint.endpointId], 
            volumeDispensedDataSeries
          );

          // Assign the appropriate y-axis based on endpoint type
          if (isEndpointBF && yAxisBF && !allAreEndpointBF) {
            volumeDispensed.yAxisId = "volumeBFAxis";
          } else {
            volumeDispensed.yAxisId = "volumeAxis";
          }

          sciChartSurface.renderableSeries.add(volumeDispensed);
      }

      // #endregion

      // #region Setup Annotation
    
      // #endregion
      
      // #region Setup the API's 
      const setAutoRange = (value: boolean) => {
        // Handle main volume axis
        const yAxisCurrent = sciChartSurface.yAxes.getById("volumeAxis");
        if (yAxisCurrent !== undefined) {
          if (value) {
            yAxisCurrent.autoRange = EAutoRange.Always;
          } else {
            yAxisCurrent.autoRange = EAutoRange.Never;

            const visibleSeries = sciChartSurface.renderableSeries.asArray().filter(x => x.isVisible && x.yAxisId === "volumeAxis");

            if (visibleSeries.length === 0) {
              yAxisCurrent.visibleRange = new NumberRange(0, 10 * 1.20);
            } else {
              var minYRange = 0
              var maxYRange = 0;
              
              visibleSeries.forEach((series) => {
                const dataSeries = series.dataSeries as XyDataSeries;
                const yRange = dataSeries.getWindowedYRange(dataSeries.xRange, true, false);
              
                if (yRange !== undefined) {
                  if (yRange.min < minYRange) {
                    minYRange = yRange.min;
                  }

                  if (yRange.max > maxYRange) {
                    maxYRange = yRange.max;
                  }
                }
              });

              yAxisCurrent.visibleRange = new NumberRange(minYRange, maxYRange * 1.20);
            }
          }
        }

        // Handle BF volume axis if it exists (only when we have mixed endpoint types)
        if (yAxisBF && !allAreEndpointBF) {
          if (value) {
            yAxisBF.autoRange = EAutoRange.Always;
          } else {
            yAxisBF.autoRange = EAutoRange.Never;

            const visibleBFSeries = sciChartSurface.renderableSeries.asArray().filter(x => x.isVisible && x.yAxisId === "volumeBFAxis");

            if (visibleBFSeries.length === 0) {
              yAxisBF.visibleRange = new NumberRange(0, 10 * 1.20);
            } else {
              var minYRangeBF = 0
              var maxYRangeBF = 0;
              
              visibleBFSeries.forEach((series) => {
                const dataSeries = series.dataSeries as XyDataSeries;
                const yRange = dataSeries.getWindowedYRange(dataSeries.xRange, true, false);
              
                if (yRange !== undefined) {
                  if (yRange.min < minYRangeBF) {
                    minYRangeBF = yRange.min;
                  }

                  if (yRange.max > maxYRangeBF) {
                    maxYRangeBF = yRange.max;
                  }
                }
              });

              yAxisBF.visibleRange = new NumberRange(minYRangeBF, maxYRangeBF * 1.20);
            }
          }
        }
      };

      setAutoRange(chartOptions.isAutoRange);

      const changeChartType = (viewType: string, visibleSeriesCompliance: any) => {
        UpdateSuspender.using(sciChartSurface, () => {
          const renderableSeriesLength = sciChartSurface.renderableSeries.asArray().length;

          for(let i = 0; i < renderableSeriesLength ; i++){
            const series = sciChartSurface.renderableSeries.get(i);

            if (series.id !== "FilterPeriodSeriesId") {
              const newVolumeDispensedSeries = createVolumeDispensedSeries(series.id, viewType, series.isVisible, series.dataSeries);
              
              // Preserve the y-axis assignment, defaulting to volumeAxis if not set
              newVolumeDispensedSeries.yAxisId = series.yAxisId || "volumeAxis";
              
              sciChartSurface.renderableSeries.set(i, newVolumeDispensedSeries);
            }
          }
        });
      };

      return { 
        sciChartSurface, 
        changeChartType, 
        setAutoRange 
      };
    };

    return {initChart, configAfterInit, setAutoRange, changeChartType};
  }

